<template>
  <PrimeDatePicker
    v-model="internalValue" v-bind="$attrs" :selection-mode="selectionMode"
    @update:model-value="handleUpdate">
    <template v-for="(_, name) in $slots" #[name]="slotProps">
      <slot :name="name" v-bind="slotProps" />
    </template>
  </PrimeDatePicker>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { DatePicker as PrimeDatePicker } from 'primevue';
import useDateFormatter from '~/composables/useDateFormatter';

/**
 * Custom DatePicker wrapper that automatically handles string date parsing
 * This component overrides PrimeVue's DatePicker to fix the issue where
 * string dates (ISO or French format) are not displayed in the calendar.
 *
 * It automatically converts string dates to Date objects for PrimeVue,
 * and optionally converts back to strings when emitting updates.
 */

defineOptions({
  inheritAttrs: false,
});

const props = defineProps<{
  modelValue?: string | Date | string[] | Date[] | null;
  selectionMode?: 'single' | 'range' | 'multiple';
  outputFormat?: 'date' | 'iso' | 'string';
}>();

const emit = defineEmits<{
  'update:modelValue': [value: string | Date | string[] | Date[] | null];
}>();

const { parseToDate, parseToDateTime } = useDateFormatter();

function toParsedDate(value: string | Date | null | undefined): Date | null {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  return parseToDate(value);
}

/**
 * Converts input value (string or Date) to Date object for PrimeVue DatePicker
 * Handles single dates, arrays, and null values
 */
const internalValue = computed({
  get() {
    if (!props.modelValue) {
      return null;
    }

    // Handle arrays (range/multiple selection)
    if (Array.isArray(props.modelValue)) {
      const parsed = props.modelValue
        .map(item => toParsedDate(item))
        .filter((date): date is Date => date !== null);

      if (parsed.length === 0) {
        return null;
      }

      if (props.selectionMode === 'single') {
        return parsed[0] ?? null;
      }

      if (props.selectionMode === 'range') {
        return parsed.slice(0, 2);
      }

      return parsed;
    }

    const parsedValue = toParsedDate(props.modelValue);

    if (!parsedValue) {
      return null;
    }

    if (props.selectionMode === 'multiple' || props.selectionMode === 'range') {
      return [parsedValue];
    }

    return parsedValue;
  },
  set(_value: Date | Date[] | null) {
    // Setter required for v-model but emission handled in handleUpdate
  },
});

/**
 * Handles the update event from PrimeVue DatePicker
 * Determines output format based on input type or explicit outputFormat prop
 */
function handleUpdate(value: Date | Date[] | null) {
  if (!value) {
    emit('update:modelValue', null);
    return;
  }
  if (Array.isArray(value) && !props.outputFormat) {
    emit('update:modelValue', value); // Preserve the same array instance for PrimeVue range mode.
    return;
  }
  // Determine output format
  let format: 'date' | 'iso' | 'string' = 'date';

  if (props.outputFormat) {
    format = props.outputFormat;
  }
  else if (typeof props.modelValue === 'string') {
    // Auto-detect format from input
    format = 'string';
  }
  else if (Array.isArray(props.modelValue) && props.modelValue.length > 0 && typeof props.modelValue[0] === 'string') {
    format = 'string';
  }

  // Handle arrays (range/multiple selection)
  if (Array.isArray(value)) {
    if (format === 'date') {
      emit('update:modelValue', value);
    }
    else {
      // Convert to string format (ISO or detect from original)
      const stringValues = value
        .map(date => formatDateToString(date))
        .filter((v): v is string => v !== null);
      emit('update:modelValue', stringValues);
    }
    return;
  }

  // Handle single value
  if (format === 'date') {
    emit('update:modelValue', value);
  }
  else {
    const formatted = formatDateToString(value);
    emit('update:modelValue', formatted);
  }
}

/**
 * Formats a Date object to string using useDateFormatter and Luxon
 * Auto-detects the format from the original modelValue or defaults to ISO
 */
function formatDateToString(date: Date | null): string | null {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    return null;
  }

  // Convert to Luxon DateTime using useDateFormatter's parseToDateTime
  const dt = parseToDateTime(date);

  if (!dt || !dt.isValid) {
    return null;
  }

  // Detect format from original input
  const originalValue = Array.isArray(props.modelValue) ? props.modelValue[0] : props.modelValue;

  if (typeof originalValue === 'string') {
    // Check if original was in French format (DD/MM/YYYY)
    if (originalValue.match(/^\d{2}\/\d{2}\/\d{4}/)) {
      return dt.toFormat('dd/MM/yyyy');
    }
  }

  // Default to ISO format (YYYY-MM-DD)
  return dt.toISODate();
}
</script>
